/** The neutral word for a code that is not known, by audience. It never carries the code. */
export const UNKNOWN_STATE_WORD = Object.freeze({
    staffShort: "Unknown",
    customer: "Updated",
    history: "Updated",
});
/** The neutral line for a `staffLong` request word that is not known. */
export const UNKNOWN_REQUEST_STAFF_LONG = "Request state unknown";
const WAITING = "Nothing for you to do right now.";
const REQUEST_STATE_ROWS = Object.freeze({
    DRAFT: {
        staffShort: "Draft",
        staffLong: "Draft request",
        customer: {
            label: "Ready to send",
            text: "This request is a draft. It has not gone to a concierge yet.",
            nextAction: "Check the details and send it to your concierge.",
            needsCustomer: true,
        },
    },
    SUBMITTED: {
        staffShort: "Submitted",
        staffLong: "Request submitted",
        customer: {
            label: "Received",
            text: "We have your request. A concierge will review it.",
            nextAction: WAITING,
            needsCustomer: false,
        },
    },
    ASSIGNED: {
        staffShort: "Assigned",
        staffLong: "Request assigned",
        customer: {
            label: "With your concierge",
            text: "Your concierge is working on this.",
            nextAction: WAITING,
            needsCustomer: false,
        },
    },
    IN_PROGRESS: {
        staffShort: "In progress",
        staffLong: "Request in progress",
        customer: {
            label: "Being arranged",
            text: "Your concierge is working on this.",
            nextAction: WAITING,
            needsCustomer: false,
        },
    },
    AWAITING_CUSTOMER: {
        staffShort: "Waiting for the customer",
        staffLong: "Request waiting for the customer",
        customer: {
            label: "Your concierge has a question",
            text: "We need a little more information from you.",
            nextAction: "Answer your concierge's question.",
            needsCustomer: true,
        },
    },
    PAYMENT_REQUIRED: {
        staffShort: "Payment required",
        staffLong: "Request: payment required",
        customer: {
            label: "Waiting for your payment",
            text: "A payment is needed to continue.",
            nextAction: "Review the payment.",
            needsCustomer: true,
        },
    },
    CONFIRMED: {
        staffShort: "Confirmed",
        staffLong: "Request confirmed",
        customer: {
            label: "Confirmed",
            text: "Confirmed by your concierge.",
            nextAction: "View the confirmation.",
            needsCustomer: false,
        },
    },
    UNAVAILABLE: {
        staffShort: "Unavailable",
        staffLong: "Request: supplier unavailable",
        customer: {
            label: "Not available",
            text: "This was not available.",
            nextAction: "Ask your concierge about other options.",
            needsCustomer: false,
        },
    },
    COMPLETED: {
        staffShort: "Completed",
        staffLong: "Request completed",
        customer: { label: "Completed", text: "Completed.", nextAction: null, needsCustomer: false },
    },
    CANCELLED: {
        staffShort: "Cancelled",
        staffLong: "Request cancelled",
        customer: { label: "Cancelled", text: "Cancelled.", nextAction: null, needsCustomer: false },
    },
    DECLINED: {
        staffShort: "Declined",
        staffLong: "Request declined",
        customer: {
            label: "Not possible",
            text: "We could not take this request.",
            nextAction: null,
            needsCustomer: false,
        },
    },
    EXPIRED: {
        staffShort: "Expired",
        staffLong: "Request expired",
        customer: {
            label: "Expired",
            text: "This request has expired.",
            nextAction: null,
            needsCustomer: false,
        },
    },
});
/** The states in which the free line of a concierge (`customerStatusSummary`) is current: the request is still being worked. */
const CONCIERGE_NOTE_STATES = new Set([
    "SUBMITTED",
    "ASSIGNED",
    "IN_PROGRESS",
    "AWAITING_CUSTOMER",
    "PAYMENT_REQUIRED",
]);
/**
 * The line a concierge wrote for the customer (`customerStatusSummary`), or `null`. The customer sees it ONLY while the request is in an
 * open working state, and only when it is not one of the closed texts. The server writes the text of SUBMITTED into that field and it
 * stays when the state moves on; a staff line such as "Your table is confirmed for 19:00" also stays when the request is later cancelled.
 * So in CONFIRMED, COMPLETED, UNAVAILABLE, DECLINED, CANCELLED and EXPIRED (and for a state that is not known) the closed status text is
 * the only status word. The details of a confirmation travel as customer updates, not here. The staff console and the app both ask this
 * function, so "what the customer sees" cannot differ between them.
 */
export function conciergeNoteOf(summary, state) {
    const known = requestStateOfCode(state);
    if (known === null || !CONCIERGE_NOTE_STATES.has(known))
        return null;
    const text = summary?.trim() ?? "";
    if (text === "")
        return null;
    return Object.values(REQUEST_STATUS_TEXT).includes(text) ? null : text;
}
/** Every request state that has words, in the order of the table. A test compares it with the state tuple of the domain. */
export const knownRequestStates = Object.freeze(Object.keys(REQUEST_STATE_ROWS));
/** The words of a customer for a code that is not known: a plain update, no claim about the request. */
export const UNKNOWN_CUSTOMER_REQUEST_WORDS = Object.freeze({
    label: UNKNOWN_STATE_WORD.customer,
    text: "Your concierge can tell you more.",
    nextAction: null,
    needsCustomer: false,
});
/** The words of the customer by state, as the closed texts `REQUEST_STATUS_TEXT` (`request-status-text.ts`) publishes them. */
export const REQUEST_STATUS_TEXT = Object.freeze(Object.fromEntries(Object.entries(REQUEST_STATE_ROWS).map(([state, row]) => [state, row.customer.text])));
/** The Action Center status code of every request state, closed ones included (history views use them). */
export const requestStatusCodes = Object.freeze({
    DRAFT: "REQUEST_DRAFT",
    SUBMITTED: "REQUEST_RECEIVED",
    ASSIGNED: "REQUEST_WITH_YOUR_CONCIERGE",
    IN_PROGRESS: "REQUEST_BEING_ARRANGED",
    AWAITING_CUSTOMER: "REQUEST_NEEDS_YOUR_INPUT",
    PAYMENT_REQUIRED: "REQUEST_AWAITING_YOUR_PAYMENT",
    CONFIRMED: "REQUEST_CONFIRMED",
    COMPLETED: "REQUEST_COMPLETED",
    CANCELLED: "REQUEST_CANCELLED",
    UNAVAILABLE: "REQUEST_UNAVAILABLE",
    DECLINED: "REQUEST_NOT_POSSIBLE",
    EXPIRED: "REQUEST_EXPIRED",
});
const requestStatesByDisplayCode = Object.freeze(Object.fromEntries(Object.entries(requestStatusCodes).map(([state, display]) => [display, state])));
/**
 * The request state behind a code that is either a state name (`AWAITING_CUSTOMER`) or an Action Center display code
 * (`REQUEST_NEEDS_YOUR_INPUT`). `null` for anything else.
 */
export function requestStateOfCode(code) {
    if (code === null || code === undefined)
        return null;
    if (Object.hasOwn(REQUEST_STATE_ROWS, code))
        return code;
    return Object.hasOwn(requestStatesByDisplayCode, code)
        ? (requestStatesByDisplayCode[code] ?? null)
        : null;
}
/** The customer words of a request. A code that is not known gives `UNKNOWN_CUSTOMER_REQUEST_WORDS`. */
export function customerRequestWords(code) {
    const state = requestStateOfCode(code);
    return state === null ? UNKNOWN_CUSTOMER_REQUEST_WORDS : REQUEST_STATE_ROWS[state].customer;
}
/** The words a staff member or the customer reads about each action of a request, by what happened. Timeline rows only. */
const REQUEST_EVENT_WORDS = Object.freeze({
    CREATED: "Created",
    DRAFT_UPDATED: "Draft updated",
    SUBMITTED: "Submitted",
    ASSIGNED: "Assigned",
    CLAIMED: "Claimed",
    AWAITING_CUSTOMER: "Asked the customer",
    CUSTOMER_RESPONDED: "Customer answered",
    RESPONSE_RECORDED: "Staff recorded an answer",
    PAYMENT_REQUIRED: "Payment required",
    PAYMENT_RESOLVED: "Payment resolved",
    PAYMENT_REPLANNED: "Payment re-planned",
    CONFIRMED: "Confirmed",
    COMPLETED: "Completed",
    CANCELLED: "Cancelled",
    UNAVAILABLE: "Marked unavailable",
    DECLINED: "Declined",
    EXPIRED: "Expired",
    OWNER_REASSIGNED: "Owner changed",
    PRIORITY_CHANGED: "Priority changed",
    STATUS_SUMMARY_UPDATED: "Customer status line changed",
    WORK_STATUS_CHANGED: "Work status changed",
});
/** The `history` word of a request event. An event that is not known gives the neutral word. */
export function requestEventWord(event) {
    return event !== null && event !== undefined && Object.hasOwn(REQUEST_EVENT_WORDS, event)
        ? REQUEST_EVENT_WORDS[event]
        : UNKNOWN_STATE_WORD.history;
}
const PAYMENT_ROWS = Object.freeze({
    DRAFT: { staffShort: "Draft", customer: "Being prepared" },
    READY: { staffShort: "Ready to pay", customer: "Ready" },
    PENDING_PAYMENT: { staffShort: "Waiting for payment", customer: "Awaiting payment" },
    PAID: { staffShort: "Paid", customer: "Paid" },
    PARTIALLY_REFUNDED: { staffShort: "Partly refunded", customer: "Partly refunded" },
    REFUNDED: { staffShort: "Refunded", customer: "Refunded" },
    FAILED: { staffShort: "Failed", customer: "Payment failed" },
    EXPIRED: { staffShort: "Expired", customer: "Expired" },
    CANCELLED: { staffShort: "Cancelled", customer: "Cancelled" },
});
/**
 * The neutral words the customer view of a payment sends (`AWAITING_PAYMENT`, `PAYMENT_FAILED`) and the status each stands for. The raw
 * statuses are still read: an older fixture or server may send them.
 */
const PAYMENT_NEUTRAL_CODES = Object.freeze({
    AWAITING_PAYMENT: "PENDING_PAYMENT",
    PAYMENT_FAILED: "FAILED",
});
/** The payment status behind a status or a neutral customer code. `null` for anything else. */
export function paymentStatusOfCode(code) {
    if (code === null || code === undefined)
        return null;
    if (Object.hasOwn(PAYMENT_ROWS, code))
        return code;
    return Object.hasOwn(PAYMENT_NEUTRAL_CODES, code) ? (PAYMENT_NEUTRAL_CODES[code] ?? null) : null;
}
const TRIP_ROWS = Object.freeze({
    PLANNING: { staffShort: "Planning", customer: "Planning" },
    UPCOMING: { staffShort: "Upcoming", customer: "Upcoming" },
    ACTIVE: { staffShort: "Under way", customer: "Under way" },
    COMPLETED: { staffShort: "Completed", customer: "Completed" },
    CANCELLED: { staffShort: "Cancelled", customer: "Cancelled" },
});
// The customer reads only a DRAFT plan and the ACTIVE ("current") one. A plan that is replaced or archived is not shown to the customer, so it
// has no customer word (`null`); asking for it gives the neutral word, never an invented one.
const ITINERARY_ROWS = Object.freeze({
    DRAFT: { staffShort: "Draft", customer: "Draft plan" },
    ACTIVE: { staffShort: "Current plan", customer: "Current plan" },
    SUPERSEDED: { staffShort: "Replaced", customer: null },
    ARCHIVED: { staffShort: "Archived", customer: null },
});
const has = (table, code) => Object.hasOwn(table, code);
/**
 * The word for one state, for one audience. The code may be `null` or a value that is not known: the answer is then the neutral word of the
 * audience, never the code. A request code may also be an Action Center display code, and a payment code a neutral customer code.
 */
export function stateWord(kind, audience, code) {
    const unknown = () => kind === "request" && audience === "staffLong"
        ? UNKNOWN_REQUEST_STAFF_LONG
        : UNKNOWN_STATE_WORD[audience === "customer" ? "customer" : "staffShort"];
    if (code === null || code === undefined)
        return unknown();
    switch (kind) {
        case "request": {
            const state = requestStateOfCode(code);
            if (state === null)
                return unknown();
            const row = REQUEST_STATE_ROWS[state];
            return audience === "customer"
                ? row.customer.label
                : row[audience];
        }
        case "payment": {
            const status = paymentStatusOfCode(code);
            return status === null
                ? unknown()
                : PAYMENT_ROWS[status][audience];
        }
        case "trip":
            return has(TRIP_ROWS, code)
                ? TRIP_ROWS[code][audience]
                : unknown();
        case "itinerary": {
            if (!has(ITINERARY_ROWS, code))
                return unknown();
            const row = ITINERARY_ROWS[code];
            return (audience === "customer" ? row.customer : row.staffShort) ?? unknown();
        }
        default:
            return unknown();
    }
}
/** Every state of a kind with its word for one audience, For a screen that lists them (a filter, a select). */
export function stateWordTable(kind, audience) {
    const states = {
        request: Object.keys(REQUEST_STATE_ROWS),
        payment: Object.keys(PAYMENT_ROWS),
        trip: Object.keys(TRIP_ROWS),
        itinerary: Object.keys(ITINERARY_ROWS),
    }[kind];
    return Object.freeze(Object.fromEntries(states.map((state) => [state, stateWord(kind, audience, state)])));
}
// --- the Action Center display codes (customer) ----------------------------------------------------------------------------------
// The status codes that the Action Center projection sends for payments, handoffs and a saved item. A request code has its words in the
// request table above. A payment is worded longer here than on a card (`Payment due`, `Payment in progress`): a row of the list names what
// the customer has to do, a chip does not.
const ACTION_STATUS_WORDS = Object.freeze({
    CONNECTING_TO_CONCIERGE: "Connecting you to your concierge",
    CONCIERGE_IN_CONVERSATION: "Your concierge is with you",
    SAVED: "Saved",
    PAYMENT_DUE: "Payment due",
    PAYMENT_IN_PROGRESS: "Payment in progress",
    PAYMENT_PAID: "Paid",
    PAYMENT_PARTLY_REFUNDED: "Partly refunded",
    PAYMENT_REFUNDED: "Refunded",
    PAYMENT_FAILED: "Payment did not go through",
    PAYMENT_EXPIRED: "Payment request expired",
    PAYMENT_CANCELLED: "Payment cancelled",
});
/** The customer word of one Action Center status code. A code that is not known gives the neutral word. */
export function actionStatusWord(code) {
    if (code === null || code === undefined)
        return UNKNOWN_STATE_WORD.customer;
    if (has(ACTION_STATUS_WORDS, code)) {
        return ACTION_STATUS_WORDS[code];
    }
    return requestStateOfCode(code) === null
        ? UNKNOWN_STATE_WORD.customer
        : customerRequestWords(code).label;
}
/** Every Action Center status code that has its own word here. A test compares it with the codes the projection can send. */
export function actionStatusCodesWithWords() {
    return [...Object.keys(ACTION_STATUS_WORDS), ...Object.values(requestStatusCodes)];
}
